import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ccoActions from "../../../../_actions/ccoActions";
import LocationMultiselect from "../_common/LocationMultiselect";
import locationActions from "../../../../_actions/locationActions";

export default function EditCcoModalV2(props) {
  const { showModal, toggleModal, ccoId, projectId } = props;

  const dispatch = useDispatch();

  // Always reload projLocationList
  useEffect(() => dispatch(locationActions.getAll(projectId)), []);
  const projLocationList = useSelector(
    (state) => state.locations.projLocationLists[projectId] || null
  );

  const ccoData = useSelector((state) =>
    state.ccos.openCcoDetails[projectId]
      ? state.ccos.openCcoDetails[projectId][ccoId] || null
      : null
  );

  const [_data, setData] = useState(ccoData ? { ...ccoData } : null);
  useEffect(() => (ccoData ? setData({ ...ccoData }) : {}), [ccoData]);

  const [ccoNo, setCcoNo] = useState((_data && _data.ccoNo) || "");
  const [supplementNo, setSupplementNo] = useState((_data && _data.supplementNo) || "0");
  const [description, setDescription] = useState((_data && _data.description) || "");

  const [locationIds, setLocationIds] = useState(
    (_data && [...(_data.locationIds || [])]) || []
  );

  useEffect(() => {
    // Check if genLoc exists. If not, insert as the first item.
    const genLoc = (projLocationList || []).find((l) => l.isGeneric);
    if (!genLoc) return;

    if (!locationIds.includes(genLoc.locationId))
      setLocationIds([genLoc.locationId, ...locationIds]);
  }, [projLocationList]);

  const [formIsIncomplete, setFormIsIncomplete] = useState(false);
  const [formErrors, setFormErrors] = useState({
    ccoNo: null,
    supplementNo: null,
    description: null,
  });

  const validateForm = () => {
    const standardError = "Required";
    const _formErrors = {
      ...formErrors,
      ccoNo: !ccoNo ? standardError : null,
      supplementNo: supplementNo ? null : standardError,
      description: description ? null : standardError,
    };
    setFormErrors(_formErrors);
  };

  useEffect(
    () => setFormIsIncomplete(Object.values(formErrors).some((e) => e)),
    [formErrors]
  );

  useEffect(() => validateForm(), []);
  useEffect(() => validateForm(), [ccoNo, supplementNo, description]);

  const handleBlur = (fieldName, value) => {
    validateForm();
    setData({ ..._data, [fieldName]: value });
  };

  const handleClick_cancel = (e) => {
    e.preventDefault();
    toggleModal();
  };

  const handleClick_save = async (e) => {
    e.preventDefault();

    if (formIsIncomplete) return;

    const _ccoData = {
      ...(ccoData || {}),
      projectId,
      ccoNo: _data.ccoNo,
      supplementNo: _data.supplementNo,
      description: _data.description,
      locationIds,
    };

    let res;
    if (ccoId) res = await dispatch(ccoActions.update(_ccoData));
    else res = await dispatch(ccoActions.add(_ccoData));

    if (res) toggleModal();
  };

  const closeBtn = (
    <button className="close" onClick={toggleModal}>
      <FontAwesomeIcon icon="times" className="fa-fw text-white" />
    </button>
  );

  return (
    <React.Fragment>
      <Modal isOpen={showModal} toggle={toggleModal} size={"xl"} fade={false}>
        <ModalHeader className="bg-info text-white" close={closeBtn}>
          <FontAwesomeIcon icon={ccoId ? "edit" : "plus"} className="fa-fw mr-2" />
          {`${ccoId ? "Edit" : "Add New"} Change Order`}
        </ModalHeader>
        <ModalBody>
          <div className="card m-2">
            <div className="card-body p-3">
              <div className="form-row mb-0">
                <div className="form-group mb-0 col-2">
                  <label htmlFor="ccoNo" className="mb-0">
                    <b>CO No.:</b>*
                  </label>
                  <input
                    type="number"
                    name="ccoNo"
                    id="ccoNo"
                    className="form-control"
                    maxLength="3"
                    value={ccoNo}
                    onChange={(e) => setCcoNo(e.target.value)}
                    onBlur={(e) => handleBlur(e.target.name, e.target.value)}
                  />
                  {formErrors.ccoNo && (
                    <span className="error-small">
                      <small>{formErrors.ccoNo}</small>
                    </span>
                  )}
                </div>
                <div className="form-group mb-0 col-2">
                  <label htmlFor="supplementNo" className="mb-0">
                    <b>Supp. No:</b>*
                  </label>
                  <input
                    type="text"
                    name="supplementNo"
                    id="supplementNo"
                    className="form-control"
                    maxLength="2"
                    value={supplementNo}
                    onChange={(e) => setSupplementNo(e.target.value)}
                    onBlur={(e) => handleBlur(e.target.name, e.target.value)}
                  />
                  {formErrors.supplementNo && (
                    <span className="error-small">
                      <small>{formErrors.supplementNo}</small>
                    </span>
                  )}
                </div>
                <div className="form-group col-8 mb-0">
                  <label htmlFor="description" className="mb-0">
                    <b>Title/Description:</b>*
                  </label>
                  <input
                    type="text"
                    name="description"
                    id="description"
                    className="form-control"
                    maxLength="300"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    onBlur={(e) => handleBlur(e.target.name, e.target.value)}
                  />
                  {formErrors.description && (
                    <span className="error-small">
                      <small>{formErrors.description}</small>
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <LocationMultiselect
            projectId={projectId}
            wpType="CO"
            locationIds={locationIds}
            setLocationIds={(locIds) => setLocationIds(locIds)}
          />
        </ModalBody>
        <ModalFooter className="bg-lightgray">
          <div className="form-row form-group mb-0">
            <button
              id="cancel"
              name="cancel"
              className="btn btn-secondary mr-2"
              onClick={handleClick_cancel}
            >
              Cancel
            </button>
            <button
              id="save"
              name="save"
              className="btn btn-primary"
              onClick={handleClick_save}
              disabled={formIsIncomplete}
            >
              <b>{ccoId ? "Save" : "Add"}</b>
            </button>
          </div>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
}
